import React, { useState, useEffect } from "react";
import { Dialog, Box, Button } from "@mui/material";
import "react-pdf/dist/Page/TextLayer.css";
import { PDFDocument } from "pdf-lib";

const PdfViewer = ({
  open,
  onClose,
  previewImage,
  useDialog,
  isPdf,
  setPageNumber,
  pageNumber,
  isVideo,
  file
}) => {
  const [fileType, setFileType] = useState("");
  const [totalPdfPages, setTotalPdfPages] = useState(1);
  const [pdfPageUrls, setPdfPageUrls] = useState([]);
  const [isPreparingPages, setIsPreparingPages] = useState(false);

  const getFileType = (url) => {
    if (url?.endsWith(".pdf") || url?.endsWith(".PDF") ) {
      return "pdf";
    } else if (
      url?.endsWith(".jpg") ||
      url?.endsWith(".jpeg") ||
      url?.endsWith(".png")
    ) {
      return "image";
    } else if (url?.endsWith(".video") ) {
      return "video"
    } else {
      return "";
    }
  };

  // Set the file type when the component mounts
  useEffect(() => {
    setFileType(getFileType(previewImage));
  }, [previewImage]);

  useEffect(() => {
    if (!open || !isPdf || !previewImage) {
      setPdfPageUrls((prevUrls) => {
        prevUrls.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });
      return;
    }

    let isMounted = true;
    setPageNumber(1);
    setIsPreparingPages(true);

    const preparePdfPages = async () => {
      try {
        const response = await fetch(previewImage);
        const bytes = await response.arrayBuffer();
        const sourcePdf = await PDFDocument.load(bytes);
        const pages = sourcePdf.getPageCount() || 1;
        const generatedUrls = [];

        for (let index = 0; index < pages; index += 1) {
          const singlePagePdf = await PDFDocument.create();
          const [copiedPage] = await singlePagePdf.copyPages(sourcePdf, [index]);
          singlePagePdf.addPage(copiedPage);
          const singlePageBytes = await singlePagePdf.save();
          const pageBlob = new Blob([singlePageBytes], { type: "application/pdf" });
          generatedUrls.push(URL.createObjectURL(pageBlob));
        }

        if (isMounted) {
          setPdfPageUrls((prevUrls) => {
            prevUrls.forEach((url) => URL.revokeObjectURL(url));
            return generatedUrls;
          });
          setTotalPdfPages(pages);
        } else {
          generatedUrls.forEach((url) => URL.revokeObjectURL(url));
        }
      } catch (_error) {
        if (isMounted) {
          setPdfPageUrls((prevUrls) => {
            prevUrls.forEach((url) => URL.revokeObjectURL(url));
            return [];
          });
          setTotalPdfPages(1);
          setPageNumber(1);
        }
      } finally {
        if (isMounted) {
          setIsPreparingPages(false);
        }
      }
    };

    preparePdfPages();

    return () => {
      isMounted = false;
    };
  }, [open, isPdf, previewImage, setPageNumber]);

  const previousPage = () => {
    setPageNumber((prevPageNumber) => Math.max(1, prevPageNumber - 1));
  };

  const nextPage = () => {
    setPageNumber((prevPageNumber) =>
      Math.min(totalPdfPages, prevPageNumber + 1)
    );
  };

  const currentPageUrl = pdfPageUrls[(pageNumber || 1) - 1];

  const renderFilePreview = () => {
    if (fileType === "pdf") {
      return (
        <embed
          src={`${previewImage}#page=${pageNumber || 1}&toolbar=0&navpanes=0&scrollbar=0`}
          type="application/pdf"
          width="100%"
          height="500px"
        />
      );
    } else if (fileType === "image") {
      return (
        <img
          src={previewImage}
          alt="File preview"
          style={{ maxWidth: "100%", maxHeight: "600px" }}
        />
      );
    } else if (fileType === "video") {
      return (
        <video 
        controls 
        style={{ 
          maxWidth: '100%', 
          maxHeight: '80vh',
          width: '100%',
          height: 'auto'
        }}
      >
        <source src={previewImage} type={file?.type} />
        Your browser does not support the video tag.
      </video>
      )
    }
  };

  if (useDialog) {
    return (
      <Dialog open={open} onClose={onClose}
      maxWidth="md" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
          maxWidth: '600px',
          overflow: 'hidden',
          display: 'flex'
        },
      }}
      >
        {isPdf ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Box
              sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f7f8fa",
              }}
            >
              <iframe
                key={`pdf-page-${pageNumber || 1}`}
                title="pdf-preview"
                src={
                  currentPageUrl
                    ? `${currentPageUrl}#zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`
                    : `${previewImage}#page=${pageNumber || 1}&zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`
                }
                scrolling="no"
                style={{
                  width: "calc(100% + 18px)",
                  height: "100%",
                  marginRight: "-18px",
                  border: "none",
                  pointerEvents: "none",
                  overflow: "hidden",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  width: "18px",
                  height: "100%",
                  backgroundColor: "#f7f8fa",
                  pointerEvents: "none",
                }}
              />
              {isPreparingPages && (
                <Box
                  sx={{
                    position: "absolute",
                    color: "#4b5563",
                    backgroundColor: "rgba(255,255,255,0.75)",
                    px: 1,
                    py: 0.5,
                    borderRadius: "4px",
                  }}
                >
                  Preparing pages...
                </Box>
              )}
            </Box>
            {totalPdfPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  py: 1,
                  flexShrink: 0,
                }}
              >
                <Button
                  onClick={previousPage}
                  disabled={(pageNumber || 1) <= 1}
                  variant="contained"
                  color="primary"
                >
                  Previous
                </Button>
                <Button
                  onClick={nextPage}
                  disabled={(pageNumber || 1) >= totalPdfPages}
                  variant="contained"
                  color="primary"
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        ) : isVideo ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            height: '100%',
            width: '100%'
          }}>
            <video 
                controls 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '80vh',
                  width: '100%',
                  height: 'auto'
                }}
            >
                <source src={previewImage} type={file?.type} />
                Your browser does not support the video tag.
            </video>
       </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            height: '100%',
            width: '100%'
          }}>
             <img 
                src={previewImage} 
                alt="Preview" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '80vh', 
                  objectFit: 'contain' 
                }} 
              />
          </Box>
        )}
      </Dialog>
    );
  } else {
    return <div>{renderFilePreview()}</div>;
  }
};

export default PdfViewer;
